"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
function OrderFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="bg-green-50/50 text-green-900 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-900">
        <CircleCheck className="size-5 text-green-600 dark:text-green-400" />
        <AlertTitle className="font-default text-lg">Takk for din bestilling!</AlertTitle>
        <AlertDescription className="mt-2">
          {successMessage}
          <div className="mt-4">
            <Button onClick={resetForm} variant="outline" className="bg-transparent border-green-300 hover:bg-green-100 dark:border-green-800 dark:hover:bg-green-900/50">
              Send en ny forespørsel
            </Button>
          </div>
        </AlertDescription>
      </Alert>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Navn er påkrevd",
        minLength: {
          value: 2,
          message: "Navnet må være minst 2 tegn"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Fullt navn</FormLabel>
              <FormControl>
                <Input placeholder="Ola Nordmann" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-post er påkrevd",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ugyldig e-postadresse"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>E-postadresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ola@eksempel.no" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefonnummer er påkrevd",
        minLength: {
          value: 8,
          message: "Ugyldig telefonnummer"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+47 123 45 678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="package" rules={{
        required: "Vennligst velg en pakke"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Valgt pakke</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Velg en pakke" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="grunnpakke">Grunnpakke</SelectItem>
                  <SelectItem value="standardpakke">Standardpakke</SelectItem>
                  <SelectItem value="premiumpakke">Premiumpakke</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <div className="md:col-span-2">
          <FormField control={control} name="message" rules={{
          required: "Melding er påkrevd",
          minLength: {
            value: 10,
            message: "Meldingen må være minst 10 tegn"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Tilleggsinformasjon / Melding</FormLabel>
                <FormControl>
                  <Textarea placeholder="Skriv inn eventuelle spørsmål eller spesielle behov her..." className="min-h-[120px] resize-y" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <Alert variant="destructive" className="mt-6">
          <CircleAlert className="size-4" />
          <AlertTitle>En feil oppstod</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="mt-8 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sender bestilling...
            </> : "Send bestilling"}
        </Button>
      </div>
    </Form>;
}
export default function FillOutOrderForm() {
  return <section id="fyll-ut-bestillingsskjemaet" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">Bestill</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Vennligst fyll ut skjemaet nedenfor for å bestille din valgte pakke. Vi vil kontakte deg kort tid etter for å bekrefte detaljene.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-sm p-6 md:p-8 lg:p-10">
          <FormProvider formId="service_form_prices" sectionName="PackageOrderForm" defaultValues={{
          name: "",
          email: "",
          phone: "",
          package: "",
          message: ""
        }} submitText="Send bestilling" successMessage="Vi har mottatt din bestilling og vil ta kontakt med deg så snart som mulig.">
            <OrderFormInner />
          </FormProvider>
        </div>
      </div>
    </section>;
}